import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Loader2, Plus, Star, Trash2, ImagePlus, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { collectExternalProductImageUrls } from "@/lib/product-external-images";
import {
  productImageDisplaySrc,
  resolveProductImageDisplayUrls,
} from "@/lib/product-image-display";

const ALLOWED_MIME = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_IMAGES = 5;

function nextAvailableSortOrder(usedOrders: number[]): number {
  const used = new Set(usedOrders);
  for (let i = 0; i < MAX_IMAGES; i++) {
    if (!used.has(i)) return i;
  }
  return MAX_IMAGES - 1;
}

async function extractInvokeErrorMessage(
  presignRes: { error?: { message?: string; context?: unknown } | null; data?: unknown },
): Promise<string> {
  const data = presignRes.data as { message?: string; error?: string } | null;
  if (data?.message) return data.message;
  if (data?.error) return data.error;

  const ctx = presignRes.error?.context as { json?: () => Promise<unknown> } | undefined;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body = (await ctx.json()) as { message?: string; error?: string };
      if (body?.message) return body.message;
      if (body?.error) return body.error;
    } catch {
      // ignore parse errors
    }
  }

  return presignRes.error?.message || "Falha ao preparar upload";
}

const IMAGE_MIME_BY_EXT: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
};

function guessMimeFromFileName(fileName: string): string | null {
  const ext = fileName.split(".").pop()?.toLowerCase() || "";
  return IMAGE_MIME_BY_EXT[ext] ?? null;
}

function resolveFileMime(file: File): string {
  if (file.type && ALLOWED_MIME.includes(file.type)) return file.type;
  return guessMimeFromFileName(file.name) || file.type;
}

function guessMimeFromUrl(url: string): string {
  try {
    const ext = new URL(url).pathname.split(".").pop()?.toLowerCase() || "";
    return IMAGE_MIME_BY_EXT[ext] || "image/jpeg";
  } catch {
    return "image/jpeg";
  }
}

interface ProductImage {
  id: string;
  product_id: string;
  workspace_id: string;
  storage_provider: string;
  object_key: string | null;
  public_url: string;
  mime_type: string;
  sort_order: number;
  alt_text: string | null;
  isExternal?: boolean;
}

interface Props {
  productId: string;
  workspaceId: string;
  disabled?: boolean;
  planBlocked?: boolean;
}

export function ProductImageGallery({
  productId,
  workspaceId,
  disabled = false,
  planBlocked = false,
}: Props) {
  const { toast } = useToast();
  const [images, setImages] = useState<ProductImage[]>([]);
  const [externalCount, setExternalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [displayUrls, setDisplayUrls] = useState<Record<string, string>>({});
  const [previewImage, setPreviewImage] = useState<{ src: string; alt: string } | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    if (!productId || !workspaceId) return;
    setLoading(true);

    const [galleryRes, productRes] = await Promise.all([
      supabase
        .from("product_images" as any)
        .select("*")
        .eq("product_id", productId)
        .eq("workspace_id", workspaceId)
        .order("sort_order", { ascending: true }),
      supabase
        .from("products")
        .select("image_url, sync_metadata, source")
        .eq("id", productId)
        .eq("workspace_id", workspaceId)
        .maybeSingle(),
    ]);

    if (galleryRes.error) {
      console.error("load product_images:", galleryRes.error);
    }

    const dbImages = ((galleryRes.data as unknown as ProductImage[]) || []).map((img) => ({
      ...img,
      isExternal: false,
    }));
    const existingUrls = new Set(dbImages.map((img) => img.public_url.trim()));

    const externalUrls = collectExternalProductImageUrls(productRes.data)
      .filter((url) => !existingUrls.has(url))
      .slice(0, MAX_IMAGES);

    const externalImages: ProductImage[] = externalUrls.map((url, index) => ({
      id: `external-${index}`,
      product_id: productId,
      workspace_id: workspaceId,
      storage_provider: "external",
      object_key: null,
      public_url: url,
      mime_type: guessMimeFromUrl(url),
      sort_order: index,
      alt_text: null,
      isExternal: true,
    }));

    setExternalCount(externalImages.length);
    const merged = [...externalImages, ...dbImages];
    setImages(merged);

    const minioIds = dbImages.map((img) => img.id);
    if (minioIds.length > 0) {
      const resolved = await resolveProductImageDisplayUrls(workspaceId, productId, minioIds);
      setDisplayUrls(resolved);
    } else {
      setDisplayUrls({});
    }

    setLoading(false);
  }, [productId, workspaceId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    if (planBlocked) {
      toast({ title: "Recurso Plus+", description: "Upgrade necessário para enviar imagens.", variant: "destructive" });
      return;
    }
    const slots = MAX_IMAGES - images.length;
    if (slots <= 0) {
      toast({ title: "Limite atingido", description: `Máximo ${MAX_IMAGES} imagens por produto.`, variant: "destructive" });
      return;
    }
    const arr = Array.from(files).slice(0, slots);
    setUploading(true);
    try {
      for (const file of arr) {
        const mimeType = resolveFileMime(file);
        if (!ALLOWED_MIME.includes(mimeType)) {
          toast({ title: "Formato inválido", description: `${file.name}: use JPG, PNG, WEBP ou GIF.`, variant: "destructive" });
          continue;
        }
        if (!Number.isFinite(file.size) || file.size <= 0) {
          toast({ title: "Arquivo inválido", description: `${file.name}: não foi possível ler o tamanho.`, variant: "destructive" });
          continue;
        }
        if (file.size > MAX_BYTES) {
          toast({ title: "Arquivo grande demais", description: `${file.name} excede 5MB.`, variant: "destructive" });
          continue;
        }

        const presignRes = await supabase.functions.invoke("presign-product-image-upload", {
          body: {
            workspace_id: workspaceId,
            product_id: productId,
            file_name: file.name,
            mime_type: mimeType,
            size: file.size,
          },
        });
        if (presignRes.error || !presignRes.data?.upload_url) {
          const msg = await extractInvokeErrorMessage(presignRes);
          toast({ title: "Erro no upload", description: msg, variant: "destructive" });
          continue;
        }

        const { upload_url, object_key, public_url, mime_type, headers } = presignRes.data as any;
        const putRes = await fetch(upload_url, {
          method: "PUT",
          headers: headers || { "Content-Type": mimeType },
          body: file,
        });
        if (!putRes.ok) {
          const detail = await putRes.text().catch(() => "");
          toast({
            title: "Falha ao enviar arquivo",
            description: detail ? `${putRes.status}: ${detail.slice(0, 120)}` : `Status ${putRes.status}`,
            variant: "destructive",
          });
          continue;
        }

        const { data: existingRows } = await supabase
          .from("product_images" as any)
          .select("sort_order")
          .eq("product_id", productId);
        const nextOrder = nextAvailableSortOrder(
          ((existingRows as unknown as { sort_order: number }[] | null) ?? []).map((row) => row.sort_order),
        );

        const { error: insertErr } = await supabase
          .from("product_images" as any)
          .insert({
            workspace_id: workspaceId,
            product_id: productId,
            storage_provider: "minio",
            object_key,
            public_url,
            mime_type,
            file_size_bytes: file.size,
            sort_order: nextOrder,
          });
        if (insertErr) {
          toast({ title: "Erro ao registrar imagem", description: insertErr.message, variant: "destructive" });
          continue;
        }
      }
      await load();
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const handleDelete = async (img: ProductImage) => {
    if (img.isExternal) {
      toast({
        title: "Imagem sincronizada",
        description: "URLs da integração (ex.: Netface) são atualizadas pelo catálogo externo.",
      });
      return;
    }
    if (!confirm("Remover esta imagem?")) return;
    const { error } = await supabase.from("product_images" as any).delete().eq("id", img.id);
    if (error) {
      toast({ title: "Erro ao remover", description: error.message, variant: "destructive" });
      return;
    }
    await load();
  };

  const setAsCover = async (img: ProductImage) => {
    if (img.isExternal) {
      toast({
        title: "Capa sincronizada",
        description: "A capa vinda da integração segue a ordem do catálogo externo.",
      });
      return;
    }
    if (img.sort_order === 0) return;
    const uploadedImages = images.filter((i) => !i.isExternal);
    const current = uploadedImages.find((i) => i.sort_order === 0);
    try {
      if (current) {
        const reordered = [
          { ...img, sort_order: 0 },
          ...uploadedImages
            .filter((i) => i.id !== img.id)
            .map((i, idx) => ({ ...i, sort_order: idx + 1 })),
        ];

        const sorted = [...reordered].sort((a, b) => b.sort_order - a.sort_order);
        for (const row of sorted) {
          const original = uploadedImages.find((i) => i.id === row.id);
          if (!original || original.sort_order === row.sort_order) continue;
          const { error: upErr } = await supabase
            .from("product_images" as any)
            .update({ sort_order: row.sort_order })
            .eq("id", row.id);
          if (upErr) throw upErr;
        }
      } else {
        await supabase.from("product_images" as any).update({ sort_order: 0 }).eq("id", img.id);
      }
      await load();
    } catch (err: any) {
      toast({ title: "Erro ao definir capa", description: err.message || String(err), variant: "destructive" });
    }
  };

  if (planBlocked) {
    return (
      <Card className="p-4 border-dashed">
        <div className="flex items-start gap-3">
          <Lock className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Galeria de imagens é um recurso Plus</p>
            <p className="text-xs text-muted-foreground">
              Faça upgrade para o plano Plus ou superior para adicionar até 5 imagens por produto
              e enviá-las no WhatsApp.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">Imagens do produto</p>
          <p className="text-xs text-muted-foreground">
            {externalCount > 0
              ? `${externalCount} URL${externalCount === 1 ? "" : "s"} sincronizada${externalCount === 1 ? "" : "s"}`
              : "Máximo"}{" "}
            {externalCount > 0 ? "·" : ""}{" "}
            {externalCount > 0
              ? `até ${MAX_IMAGES} uploads · JPG, PNG, WEBP ou GIF · até 5MB cada`
              : `${MAX_IMAGES} imagens · JPG, PNG, WEBP ou GIF · até 5MB cada`}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled || uploading || images.length >= MAX_IMAGES}
          onClick={() => inputRef.current?.click()}
        >
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Adicionar
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED_MIME.join(",")}
          multiple
          className="hidden"
          onChange={(e) => void handleFiles(e.target.files)}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : images.length === 0 ? (
        <Card className="p-6 border-dashed text-center text-sm text-muted-foreground">
          <ImagePlus className="h-6 w-6 mx-auto mb-2 opacity-60" />
          Nenhuma imagem ainda. Clique em "Adicionar" para enviar até {MAX_IMAGES} fotos.
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {images.map((img) => {
            const displaySrc = productImageDisplaySrc(img, displayUrls);
            return (
            <div
              key={img.id}
              role="button"
              tabIndex={0}
              className={cn(
                "relative group rounded-lg overflow-hidden border bg-muted cursor-zoom-in",
                img.sort_order === 0 && !img.isExternal && "ring-2 ring-primary",
                img.isExternal && img.sort_order === 0 && "ring-2 ring-muted-foreground/40",
              )}
              onClick={() => setPreviewImage({ src: displaySrc, alt: img.alt_text || "Imagem do produto" })}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setPreviewImage({ src: displaySrc, alt: img.alt_text || "Imagem do produto" });
                }
              }}
            >
              <img
                src={displaySrc}
                alt={img.alt_text || ""}
                className="w-full aspect-square object-cover"
                loading="lazy"
                onError={() => {
                  if (img.isExternal) return;
                  void (async () => {
                    const resolved = await resolveProductImageDisplayUrls(
                      workspaceId,
                      productId,
                      [img.id],
                    );
                    if (resolved[img.id]) {
                      setDisplayUrls((prev) => ({ ...prev, [img.id]: resolved[img.id] }));
                    }
                  })();
                }}
              />
              {img.sort_order === 0 && (
                <span className="absolute top-1 left-1 text-[10px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded pointer-events-none">
                  {img.isExternal ? "Capa sync" : "Capa"}
                </span>
              )}
              {img.isExternal && img.sort_order !== 0 && (
                <span className="absolute top-1 left-1 text-[10px] bg-muted-foreground/80 text-background px-1.5 py-0.5 rounded pointer-events-none">
                  Sync
                </span>
              )}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 pointer-events-none">
                {!img.isExternal && img.sort_order !== 0 && (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    className="h-8 w-8 pointer-events-auto"
                    title="Definir como capa"
                    onClick={(e) => {
                      e.stopPropagation();
                      void setAsCover(img);
                    }}
                  >
                    <Star className="h-4 w-4" />
                  </Button>
                )}
                {!img.isExternal && (
                  <Button
                    type="button"
                    size="icon"
                    variant="destructive"
                    className="h-8 w-8 pointer-events-auto"
                    title="Remover"
                    onClick={(e) => {
                      e.stopPropagation();
                      void handleDelete(img);
                    }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
                {img.isExternal && (
                  <span className="text-[10px] text-white bg-black/50 px-2 py-1 rounded flex items-center gap-1 pointer-events-none">
                    <Lock className="h-3 w-3" />
                    URL externa
                  </span>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-2 sm:p-4 bg-black/95 border-none flex items-center justify-center">
          <DialogTitle className="sr-only">Visualização da imagem do produto</DialogTitle>
          {previewImage && (
            <img
              src={previewImage.src}
              alt={previewImage.alt}
              className="max-w-[90vw] max-h-[85vh] object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
